import http from "../utilities/HttpModule";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getVariableFromEnv, getServiceUrl } from "../utilities/env";
import {
  canRefreshToken,
  requestTokenRefresh,
  waitForTokenRefresh,
} from "../utilities/tokenRefresh";

const API_BASE_URL = getVariableFromEnv("API_BASE_URL");

/** The exact parameter tuple of the query function `fetchBaseQuery` returns. */
type BaseQueryParams = Parameters<ReturnType<typeof fetchBaseQuery>>;

export const getHeadersWithAuthorization = (headers: Headers) => {
  const token = http.axios.defaults.headers.common.Authorization;
  if (token) {
    headers.set("Authorization", token as string);
  }
  return headers;
};

export const parseReduxQueryError = (err: unknown) => {
  let errorMessage: unknown;
  // Callers pass a value caught from `queryFulfilled`, which RTK Query always
  // rejects with an object (usually `{ error, meta, isUnhandledError }`). The
  // casts keep the original `in`-check semantics (which throw on a non-object)
  // rather than silently changing behaviour for inputs that cannot occur here.
  if ("error" in (err as object)) {
    errorMessage = (err as { error: unknown }).error;
  } else {
    errorMessage = err;
  }

  return JSON.stringify(errorMessage);
};

export const customBaseQuery = (
  service?: Parameters<typeof getServiceUrl>[0],
) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: service ? getServiceUrl(service) : API_BASE_URL,
    prepareHeaders: (headers) => getHeadersWithAuthorization(headers),
  });

  return async (
    args: BaseQueryParams[0],
    api: BaseQueryParams[1],
    extraOptions: BaseQueryParams[2],
  ) => {
    let result = await baseQuery(args, api, extraOptions);

    // Intercept 401 (Unauthorized) responses before any endpoint-specific logic executes.
    if (result.error && result.error.status === 401 && canRefreshToken()) {
      requestTokenRefresh();

      try {
        await waitForTokenRefresh();
        // Retry the original request with the refreshed token
        result = await baseQuery(args, api, extraOptions);
      } catch {
        // Token refresh timed out — return the original 401 error
      }
    }

    return result;
  };
};
